'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';

import { trackSidebarToggle } from '@/app/lib/trackNav';

interface SidebarContextType {
  collapsed: boolean;
  mobileOpen: boolean;
  toggle: () => void;
  toggleMobile: () => void;
  closeMobile: () => void;
}

const SidebarContext = createContext<SidebarContextType>({
  collapsed: false,
  mobileOpen: false,
  toggle: () => {},
  toggleMobile: () => {},
  closeMobile: () => {},
});

const STORAGE_KEY = 'mcworldtools_sidebar_collapsed';

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'true') setCollapsed(true);
    } catch {}
  }, []);

  // The current value, readable from a callback without making `collapsed` a dependency.
  const collapsedRef = useRef(collapsed);
  useEffect(() => { collapsedRef.current = collapsed; }, [collapsed]);

  // ⚠️ The side effects live OUT here, not inside a setState updater. An updater must be pure:
  // React may invoke it more than once for a single update, which would write localStorage twice
  // and — now that there is one — fire the analytics event twice. This codebase has already been
  // bitten by a side effect inside an updater once (see useSavedElements), so the ref pattern
  // above is the established fix rather than a new invention.
  const toggle = useCallback(() => {
    const next = !collapsedRef.current;
    setCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, String(next)); } catch {}
    trackSidebarToggle(next);
  }, []);

  const toggleMobile = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // The drawer only exists below lg (1024px). An open drawer that crosses into lg (tablet rotated,
  // window widened) would otherwise stay "open": body scroll locked and the rail showing labels.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => { if (e.matches) setMobileOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return (
    <SidebarContext.Provider value={{ collapsed, mobileOpen, toggle, toggleMobile, closeMobile }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  return useContext(SidebarContext);
}
